import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, History, RotateCcw, Save, X } from 'lucide-react';

import { ConfirmDialog } from '@/components/data/ConfirmDialog';
import { ContentGuardWarning } from '@/components/forms/ContentGuardWarning';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { isCopyConflict } from '@/api/admin/copy';
import { DateTime } from '@/components/data/DateTime';
import { cn } from '@/lib/cn';
import type { GuardHit } from '@/lib/contentGuard';
import {
  copyValueChanged,
  validateCopyValue,
  type CopyIssue,
  type CopyValidationResult,
} from '@/lib/copyValidation';
import { useApiError } from '@/lib/useApiError';
import { useClearCopyOverride, useCopyKey, useSaveCopyDraft } from '@/queries/copy';
import {
  COPY_LOCALES,
  baseVersionFor,
  effectiveValue,
  localeStatus,
  type CopyItem,
  type CopyKey,
  type CopyLocale,
  type CopyShape,
  type CopyValue,
} from '@/schemas/admin/copy';
import { BaselineDiff } from './BaselineDiff';
import { CopyStatusChip } from './CopyStatusChip';
import { ObjectListEditor } from './ObjectListEditor';
import { StringEditor } from './StringEditor';
import { StringListEditor } from './StringListEditor';
import { fieldErrorMap, useCopyIssueText } from './useCopyIssueText';

type DraftMap = Record<CopyLocale, CopyValue>;

function emptyFor(shape: CopyShape): CopyValue {
  return shape === 'string' ? '' : [];
}

/** Seed every locale from what the site serves today: draft → published → baseline. */
function seedDraft(key: CopyKey): DraftMap {
  const out = {} as DraftMap;
  for (const locale of COPY_LOCALES) {
    out[locale] = effectiveValue(key, locale).value ?? emptyFor(key.shape);
  }
  return out;
}

/** What the *site* renders now — the diff's left-hand side, ignoring the draft. */
function servedValue(key: CopyKey, locale: CopyLocale): CopyValue | null {
  const state = key.locales[locale];
  if (state?.published_value != null) return state.published_value;
  return key.baseline[locale] ?? null;
}

function fingerprintOf(key: CopyKey): string {
  return `${key.key}|${COPY_LOCALES.map((l) => key.locales[l]?.version ?? 0).join(',')}`;
}

function guardHitsOf(result: CopyValidationResult): GuardHit[] {
  const hits: GuardHit[] = [];
  for (const issue of result.errors) {
    if (issue.code === 'carveout_violation' && issue.token) {
      hits.push({ term: issue.token, severity: 'banned' });
    }
  }
  for (const issue of result.warnings) {
    if (issue.code === 'retired_framing' && issue.token) {
      hits.push({ term: issue.token, severity: 'retired' });
    }
  }
  return hits;
}

interface CopyEditorDrawerProps {
  /** Catalog key being edited, or `null` when the drawer is closed. */
  copyKey: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Edit one catalog key across all three locales.
 *
 * Everything here is draft-only: a save writes `status=draft` and nothing
 * reaches the public site until the publish bar runs a batch publish (plan Q2).
 * The drawer owns the active locale so the status strip, the editor, the
 * baseline and the diff all move together.
 */
export function CopyEditorDrawer({ copyKey, open, onOpenChange }: CopyEditorDrawerProps) {
  const { t } = useTranslation('copy');
  const { t: tc } = useTranslation('common');
  const resolveError = useApiError();
  const issueText = useCopyIssueText();

  const detailQuery = useCopyKey(open ? copyKey : null);
  const detail = detailQuery.data?.key.key === copyKey ? detailQuery.data : null;
  const key = detail?.key ?? null;

  const save = useSaveCopyDraft();
  const clear = useClearCopyOverride();

  const [activeLocale, setActiveLocale] = React.useState<CopyLocale>('en');
  const [draft, setDraft] = React.useState<DraftMap | null>(null);
  const [initial, setInitial] = React.useState<DraftMap | null>(null);
  const [attempted, setAttempted] = React.useState(false);
  const [conflict, setConflict] = React.useState<CopyLocale | null>(null);
  const [staleBanner, setStaleBanner] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [confirmRevert, setConfirmRevert] = React.useState<CopyLocale | null>(null);
  const appliedRef = React.useRef<string>('');

  const fingerprint = key ? fingerprintOf(key) : '';

  const dirtyLocales = React.useMemo(() => {
    if (!draft || !initial) return [] as CopyLocale[];
    return COPY_LOCALES.filter((l) => copyValueChanged(initial[l], draft[l]));
  }, [draft, initial]);

  // Seed on open / key change, and re-seed after a server-side change — but
  // never on top of unsaved edits: an operator who is mid-sentence gets a
  // "reload" banner instead of a silently rewritten textarea.
  React.useEffect(() => {
    if (!key) return;
    if (appliedRef.current === fingerprint) return;
    const sameKey = appliedRef.current.split('|')[0] === key.key;
    if (appliedRef.current !== '' && sameKey && dirtyLocales.length > 0) {
      setStaleBanner(true);
      return;
    }
    const seeded = seedDraft(key);
    setDraft(seeded);
    setInitial(seedDraft(key));
    setAttempted(false);
    setConflict(null);
    setStaleBanner(false);
    setError(null);
    appliedRef.current = fingerprint;
  }, [key, fingerprint, dirtyLocales.length]);

  React.useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onOpenChange(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  const validation = React.useMemo(() => {
    const out = {} as Record<CopyLocale, CopyValidationResult>;
    for (const locale of COPY_LOCALES) {
      if (!key || !draft) {
        out[locale] = { errors: [], warnings: [], ok: true };
        continue;
      }
      out[locale] = validateCopyValue({
        shape: key.shape,
        itemFields: key.item_fields,
        maxLen: key.max_len,
        baseline: key.baseline[locale] ?? null,
        value: draft[locale] ?? emptyFor(key.shape),
      });
    }
    return out;
  }, [key, draft]);

  if (!open || !copyKey) return null;

  const activeResult = validation[activeLocale];
  const activeDirty = dirtyLocales.includes(activeLocale);
  const showIssues = activeDirty || attempted;
  const activeErrors: CopyIssue[] = showIssues ? activeResult.errors : [];
  const activeWarnings: CopyIssue[] = showIssues ? activeResult.warnings : [];
  const guardHits = showIssues ? guardHitsOf(activeResult) : [];
  const blockedLocales = dirtyLocales.filter((l) => !validation[l].ok);
  const saving = save.isPending;

  function setLocaleValue(locale: CopyLocale, next: CopyValue) {
    setDraft((prev) => (prev ? { ...prev, [locale]: next } : prev));
  }

  async function onSave() {
    if (!key || !draft) return;
    setAttempted(true);
    setError(null);
    if (dirtyLocales.length === 0) return;
    if (blockedLocales.length > 0) {
      // AC7/AC8: a value that cannot succeed never leaves the browser.
      const first = blockedLocales[0]!;
      setActiveLocale(first);
      toast.error(t('drawer.blockedBySelf'));
      return;
    }
    const warnings: string[] = [];
    for (const locale of dirtyLocales) {
      try {
        const res = await save.mutateAsync({
          key: key.key,
          locale,
          input: { value: draft[locale], base_version: baseVersionFor(key, locale) },
        });
        for (const w of res.warnings) warnings.push(w.message);
      } catch (e) {
        if (isCopyConflict(e)) {
          setConflict(locale);
          return;
        }
        setError(resolveError(e).message);
        return;
      }
    }
    // Our own write is not a "someone else edited this" event.
    setInitial(draft);
    appliedRef.current = '';
    toast.success(t('drawer.saved', { count: dirtyLocales.length }));
    if (warnings.length > 0) toast.warning(warnings.join(' · '));
  }

  async function onRevert(locale: CopyLocale) {
    if (!key) return;
    setConfirmRevert(null);
    setError(null);
    try {
      await clear.mutateAsync({ key: key.key, locale });
      setInitial(draft);
      appliedRef.current = '';
      toast.success(t('drawer.reverted', { locale: locale.toUpperCase() }));
    } catch (e) {
      setError(resolveError(e).message);
    }
  }

  function reload() {
    appliedRef.current = '';
    setInitial(draft);
    setConflict(null);
    setStaleBanner(false);
    void detailQuery.refetch();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('drawer.title')}
      className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <aside className="relative flex h-full w-full max-w-3xl flex-col border-s border-[color:var(--color-border)] bg-[color:var(--color-surface)] shadow-xl">
        <header className="flex items-start justify-between gap-3 border-b border-[color:var(--color-border)] p-6 pb-4">
          <div className="min-w-0 space-y-1">
            <h2 className="text-lg font-semibold">{t('drawer.title')}</h2>
            <p dir="ltr" className="break-all font-mono text-xs text-[color:var(--color-fg-muted)]">
              {copyKey}
            </p>
            {key ? (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Badge tone="neutral">{t('meta.surface', { surface: key.surface })}</Badge>
                <Badge tone="neutral">{t(`shape.${key.shape}`)}</Badge>
                {key.orphaned ? <Badge tone="warn">{t('flags.orphaned')}</Badge> : null}
                {key.baseline_changed ? <Badge tone="info">{t('flags.baseline_changed')}</Badge> : null}
                {key.needs_eg_ar ? <Badge tone="accent">{t('flags.needs_eg_ar')}</Badge> : null}
              </div>
            ) : null}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            aria-label={tc('actions.close')}
          >
            <X className="size-5" aria-hidden />
          </Button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto p-6">
          {detailQuery.isPending ? (
            <p className="text-sm text-[color:var(--color-fg-muted)]">{tc('states.loading')}</p>
          ) : null}
          {detailQuery.isError ? (
            <p role="alert" className="text-sm text-[color:var(--color-danger)]">
              {resolveError(detailQuery.error).message}
            </p>
          ) : null}

          {conflict ? (
            <Banner
              tone="danger"
              title={t('drawer.conflictTitle')}
              body={t('drawer.conflictBody', { locale: conflict.toUpperCase() })}
              actionLabel={t('drawer.reload')}
              onAction={reload}
            />
          ) : null}
          {staleBanner && !conflict ? (
            <Banner
              tone="warn"
              title={t('drawer.staleTitle')}
              body={t('drawer.staleBody')}
              actionLabel={t('drawer.reload')}
              onAction={reload}
            />
          ) : null}
          {error ? (
            <Banner tone="danger" title={tc('states.error')} body={error} />
          ) : null}

          {key && draft ? (
            <>
              <LocaleStrip
                copyKey={key}
                active={activeLocale}
                dirty={dirtyLocales}
                invalid={blockedLocales}
                onSelect={setActiveLocale}
              />

              {key.shape === 'string' ? (
                <StringEditor
                  id={`copy-${key.key}`}
                  label={t('editor.valueLabel')}
                  values={{
                    en: typeof draft.en === 'string' ? draft.en : '',
                    ar: typeof draft.ar === 'string' ? draft.ar : '',
                    'eg-ar': typeof draft['eg-ar'] === 'string' ? draft['eg-ar'] : '',
                  }}
                  onChange={(locale, next) => setLocaleValue(locale, next)}
                  activeLocale={activeLocale}
                  onActiveLocaleChange={setActiveLocale}
                  baseline={{
                    en: typeof key.baseline.en === 'string' ? key.baseline.en : '',
                    ar: typeof key.baseline.ar === 'string' ? key.baseline.ar : '',
                    'eg-ar':
                      typeof key.baseline['eg-ar'] === 'string' ? key.baseline['eg-ar'] : '',
                  }}
                  maxLen={key.max_len}
                  placeholders={key.placeholders}
                  errors={
                    activeErrors.length > 0 ? { [activeLocale]: issueText(activeErrors[0]!) } : {}
                  }
                />
              ) : null}

              {key.shape === 'string_list' ? (
                <StringListEditor
                  id={`copy-${key.key}`}
                  label={t('editor.valueLabel')}
                  locale={activeLocale}
                  value={Array.isArray(draft[activeLocale]) ? (draft[activeLocale] as string[]) : []}
                  onChange={(next) => setLocaleValue(activeLocale, next)}
                  baseline={
                    Array.isArray(key.baseline[activeLocale])
                      ? (key.baseline[activeLocale] as string[])
                      : []
                  }
                  maxLen={key.max_len}
                  fieldErrors={fieldErrorMap(activeErrors, issueText)}
                />
              ) : null}

              {key.shape === 'object_list' ? (
                <ObjectListEditor
                  id={`copy-${key.key}`}
                  label={t('editor.valueLabel')}
                  locale={activeLocale}
                  value={
                    Array.isArray(draft[activeLocale])
                      ? (draft[activeLocale] as CopyItem[])
                      : []
                  }
                  onChange={(next) => setLocaleValue(activeLocale, next)}
                  baseline={
                    Array.isArray(key.baseline[activeLocale])
                      ? (key.baseline[activeLocale] as CopyItem[])
                      : []
                  }
                  itemFields={key.item_fields}
                  maxLen={key.max_len}
                  fieldErrors={fieldErrorMap(activeErrors, issueText)}
                />
              ) : null}

              {guardHits.length > 0 ? <ContentGuardWarning hits={guardHits} /> : null}

              {activeErrors.length > 0 ? (
                <IssueList tone="danger" title={t('drawer.errorsTitle')} issues={activeErrors} render={issueText} />
              ) : null}
              {activeWarnings.length > 0 ? (
                <IssueList tone="warn" title={t('drawer.warningsTitle')} issues={activeWarnings} render={issueText} />
              ) : null}

              <section>
                <h3 className="mb-2 text-sm font-semibold">{t('drawer.diffTitle')}</h3>
                <BaselineDiff
                  shape={key.shape}
                  locale={activeLocale}
                  before={servedValue(key, activeLocale)}
                  beforeLabel={t('diff.live')}
                  after={draft[activeLocale] ?? null}
                  afterLabel={t('diff.draft')}
                />
              </section>

              {detail && detail.revisions.length > 0 ? (
                <section>
                  <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
                    <History className="size-4" aria-hidden />
                    {t('drawer.historyTitle')}
                  </h3>
                  <ul className="space-y-1 text-xs text-[color:var(--color-fg-muted)]">
                    {detail.revisions.slice(0, 8).map((rev) => (
                      <li key={rev.id} className="flex flex-wrap items-center gap-2">
                        <span dir="ltr" className="font-mono uppercase">
                          {rev.locale}
                        </span>
                        <span>{t(`revision.${rev.action}`, { defaultValue: rev.action })}</span>
                        <DateTime value={rev.created_at} />
                        {rev.bundle_version != null ? (
                          <Badge tone="neutral">{t('meta.bundleV', { version: rev.bundle_version })}</Badge>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
            </>
          ) : null}
        </div>

        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-[color:var(--color-border)] p-4">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmRevert(activeLocale)}
              disabled={!key || localeStatus(key, activeLocale) === 'clean' || clear.isPending}
            >
              <RotateCcw className="size-4" aria-hidden />
              {t('drawer.revertToBaseline', { locale: activeLocale.toUpperCase() })}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[color:var(--color-fg-muted)]">
              {dirtyLocales.length > 0
                ? t('drawer.unsaved', { count: dirtyLocales.length })
                : t('drawer.noChanges')}
            </span>
            <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)}>
              {tc('actions.close')}
            </Button>
            <Button
              size="sm"
              onClick={() => void onSave()}
              loading={saving}
              disabled={dirtyLocales.length === 0 || saving}
            >
              <Save className="size-4" aria-hidden />
              {t('drawer.saveDraft')}
            </Button>
          </div>
        </footer>
      </aside>

      <ConfirmDialog
        open={confirmRevert !== null}
        onOpenChange={(next) => setConfirmRevert(next ? confirmRevert : null)}
        title={t('drawer.revertConfirmTitle')}
        description={t('drawer.revertConfirmBody', {
          locale: (confirmRevert ?? activeLocale).toUpperCase(),
        })}
        confirmLabel={t('drawer.revertConfirmAction')}
        destructive
        loading={clear.isPending}
        onConfirm={() => onRevert(confirmRevert ?? activeLocale)}
      />
    </div>
  );
}

/** Locale switcher: status per locale, plus unsaved / invalid markers. */
function LocaleStrip({
  copyKey,
  active,
  dirty,
  invalid,
  onSelect,
}: {
  copyKey: CopyKey;
  active: CopyLocale;
  dirty: readonly CopyLocale[];
  invalid: readonly CopyLocale[];
  onSelect: (locale: CopyLocale) => void;
}) {
  const { t } = useTranslation('copy');
  return (
    <div role="tablist" aria-label={t('drawer.localeTablist')} className="flex flex-wrap gap-2">
      {COPY_LOCALES.map((locale) => {
        const selected = locale === active;
        return (
          <button
            key={locale}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onSelect(locale)}
            className={cn(
              'flex items-center gap-2 rounded-[var(--radius-md)] border px-3 py-2 text-start',
              selected
                ? 'border-[color:var(--color-accent)] bg-[color:var(--color-accent-soft)]'
                : 'border-[color:var(--color-border)] bg-[color:var(--color-surface-2)] hover:bg-[color:var(--color-surface-3)]'
            )}
          >
            <CopyStatusChip locale={locale} status={localeStatus(copyKey, locale)} />
            {invalid.includes(locale) ? (
              <span className="flex items-center gap-1 text-xs font-semibold text-[color:var(--color-danger)]">
                <AlertTriangle className="size-3.5" aria-hidden />
                {t('drawer.invalid')}
              </span>
            ) : dirty.includes(locale) ? (
              <span className="text-xs font-semibold text-[color:var(--color-warn)]">
                {t('drawer.unsavedShort')}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function IssueList({
  tone,
  title,
  issues,
  render,
}: {
  tone: 'danger' | 'warn';
  title: string;
  issues: readonly CopyIssue[];
  render: (issue: CopyIssue) => string;
}) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'rounded-[var(--radius-md)] border p-3 text-xs',
        tone === 'danger'
          ? 'border-[color:var(--color-danger)] bg-[color:var(--color-danger-soft)]'
          : 'border-[color:var(--color-warn)] bg-[color:var(--color-warn-soft)]'
      )}
    >
      <p className="mb-1 font-semibold text-[color:var(--color-fg)]">{title}</p>
      <ul className="list-inside list-disc space-y-0.5 text-[color:var(--color-fg)]">
        {issues.map((issue, i) => (
          <li key={`${issue.code}-${issue.field}-${i}`}>
            {issue.field ? (
              <span dir="ltr" className="me-1 font-mono text-[11px]">
                {issue.field}
              </span>
            ) : null}
            {render(issue)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Banner({
  tone,
  title,
  body,
  actionLabel,
  onAction,
}: {
  tone: 'danger' | 'warn';
  title: string;
  body: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-wrap items-start gap-3 rounded-[var(--radius-md)] border p-3 text-sm',
        tone === 'danger'
          ? 'border-[color:var(--color-danger)] bg-[color:var(--color-danger-soft)]'
          : 'border-[color:var(--color-warn)] bg-[color:var(--color-warn-soft)]'
      )}
    >
      <AlertTriangle
        className={cn(
          'mt-0.5 size-5 shrink-0',
          tone === 'danger' ? 'text-[color:var(--color-danger)]' : 'text-[color:var(--color-warn)]'
        )}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-[color:var(--color-fg)]">{title}</p>
        <p className="text-[color:var(--color-fg-muted)]">{body}</p>
      </div>
      {actionLabel && onAction ? (
        <Button variant="outline" size="sm" onClick={onAction}>
          {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}
